/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { DateTime } from '@easepick/bundle';
import { USE_OBSERVABLE_STATE_COMPLETE, USE_OBSERVABLE_STATE_PENDING } from '@squiz/dxp-console-support';
import {
  DASHBOARD_SUMMARY_TIME_PERIOD,
  DashboardSummary,
  DashboardSummaryItem,
  DatetimePicker,
  DatetimePickerProps,
  DatetimePickerResult,
  EmptyState,
  EmptyStateProps,
  ErrorMessage,
  FormControl,
  ICON_CHART_WITH_DOWNLOADING,
  Loader,
  SingleDatetimePickerResult,
  Tabs,
  TabsProps,
} from '@squiz/sds';
import { MetricCollectionServiceGetMetricStatsDto } from 'apps/console-core/src/Services/metric-collection';
import {
  METRIC_STATS_RANGES,
  METRIC_STATS_TYPES,
  MetricStatValue,
} from 'apps/console-core/src/Services/metric-collection/src/Entities/MetricStatsItem/MetricStatsItem.entity';
import { useMetricStats } from 'apps/console-core/src/Services/metric-collection/src/Hooks/useMetricStats/useMetricStats';
import { MONITORING_STRINGS } from 'apps/monitoring/src/Constants/Strings.constants';
import { differenceInDays, subDays } from 'date-fns';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileSizeTable } from '../FileSizeTable/FileSizeTable';
import { NotFoundErrorTable } from '../NotFoundErrorTable/NotFoundErrorTable';
import { PageLoadTable } from '../PageLoadTable/PageLoadTable';
import { PageViewsTable } from '../PageViewsTable/PageViewsTable';
import { EM_PAGE_PREVIEW_CONTENT_STRINGS } from './ExperienceMonitoringPagePreviewContent.constants';
import { ExperienceMonitoringPagePreviewContentProps } from './ExperienceMonitoringPagePreviewContent.props';

export const ExperienceMonitoringPagePreviewContent = ({
  host,
}: ExperienceMonitoringPagePreviewContentProps): JSX.Element => {
  const today = useMemo(() => Date.now(), []);
  const [datetime, setDatetime] = useState(today);
  const [endDatetime, setEndDatetime] = useState(today);
  const [startDatetime, setStartDatetime] = useState(subDays(today, 1).getTime());

  const [range, setRange] = useState<boolean>(false);
  const handleSetRange = (): void => {
    setRange(!range);
    setStartDatetime(subDays(today, 1).setHours(0, 0, 0, 0));
    setEndDatetime(new DateTime(today).setHours(23, 59, 59, 999));
  };
  const { t } = useTranslation();
  const labels = useMemo(() => {
    return {
      '404Errors': t(EM_PAGE_PREVIEW_CONTENT_STRINGS['404Errors']),
      averagePageLoadTime: t(EM_PAGE_PREVIEW_CONTENT_STRINGS.averagePageLoadTime),
      cacheHitRatio: t(EM_PAGE_PREVIEW_CONTENT_STRINGS.cacheHitRatio),
      cacheHitRatioTooltipText: t(EM_PAGE_PREVIEW_CONTENT_STRINGS.cacheHitRatioTooltipText),
      date: t(MONITORING_STRINGS.date),
      dateRange: t(MONITORING_STRINGS.dateRange),
      emptyStateCaption: t(EM_PAGE_PREVIEW_CONTENT_STRINGS.emptyStateCaption),
      emptyStateTitle: t(EM_PAGE_PREVIEW_CONTENT_STRINGS.emptyStateTitle),
      errorMessage: t(EM_PAGE_PREVIEW_CONTENT_STRINGS.errorMessage),
      fileSize: t(EM_PAGE_PREVIEW_CONTENT_STRINGS.fileSize),
      httpErrorRates: t(EM_PAGE_PREVIEW_CONTENT_STRINGS.httpErrorRates),
      httpErrorRatesTooltipText: t(EM_PAGE_PREVIEW_CONTENT_STRINGS.httpErrorRatesTooltipText),
      pageLoadTime: t(EM_PAGE_PREVIEW_CONTENT_STRINGS.pageLoadTime),
      pageLoadTimeTooltipText: t(EM_PAGE_PREVIEW_CONTENT_STRINGS.pageLoadTimeTooltipText),
      pageViews: t(EM_PAGE_PREVIEW_CONTENT_STRINGS.pageViews),
      pageViewsTooltipText: t(EM_PAGE_PREVIEW_CONTENT_STRINGS.pageViewsTooltipText),
      popularPages: t(EM_PAGE_PREVIEW_CONTENT_STRINGS.popularPages),
      totalErrors: t(EM_PAGE_PREVIEW_CONTENT_STRINGS.totalErrors),
      totalPageViews: t(EM_PAGE_PREVIEW_CONTENT_STRINGS.totalPageViews),
      useCustomRange: t(EM_PAGE_PREVIEW_CONTENT_STRINGS.useCustomRange),
    };
  }, [t]);

  const calculateDateDifference = useMemo(() => {
    return differenceInDays(endDatetime, startDatetime);
  }, [endDatetime, startDatetime]);

  const getDateFrom: number = useMemo(() => {
    if (range) {
      return startDatetime;
    }
    return new DateTime(datetime).setHours(0, 0, 0, 0);
  }, [datetime, range, startDatetime]);

  const getDateTo: number = useMemo(() => {
    if (range) {
      return endDatetime;
    }
    return new DateTime(datetime).setHours(23, 59, 59, 999);
  }, [datetime, endDatetime, range]);

  const getCurrentStatsProps: MetricCollectionServiceGetMetricStatsDto = {
    dateFrom: getDateFrom,
    dateTo: getDateTo,
    host,
    range: METRIC_STATS_RANGES.daily,
    type: [
      METRIC_STATS_TYPES.avgCacheRatio,
      METRIC_STATS_TYPES.avgPageLoadTime,
      METRIC_STATS_TYPES.avgHttpError,
      METRIC_STATS_TYPES.httpStatus404,
      METRIC_STATS_TYPES.cacheRatio,
      METRIC_STATS_TYPES.fileSize,
      METRIC_STATS_TYPES.pageLoadTime,
      METRIC_STATS_TYPES.pageViews,
      METRIC_STATS_TYPES.summaryPageViews,
    ],
  };

  const getPrevStatsProps: MetricCollectionServiceGetMetricStatsDto = {
    dateFrom: range
      ? subDays(getCurrentStatsProps.dateFrom, calculateDateDifference).setHours(0, 0, 0, 0)
      : subDays(getCurrentStatsProps.dateFrom, 1).getTime(),
    dateTo: range ? getCurrentStatsProps.dateFrom : subDays(getCurrentStatsProps.dateTo, 1).getTime(),
    host,
    range: METRIC_STATS_RANGES.daily,
    type: [METRIC_STATS_TYPES.avgPageLoadTime, METRIC_STATS_TYPES.summaryPageViews, METRIC_STATS_TYPES.avgCacheRatio],
  };

  const getTimeSeriesStatsProps: MetricCollectionServiceGetMetricStatsDto = {
    dateFrom: subDays(today, 30).getTime(),
    dateTo: today,
    host,
    range: METRIC_STATS_RANGES.daily,
    type: [METRIC_STATS_TYPES.timeSeriesHttp404Errors, METRIC_STATS_TYPES.timeSeriesPageViews],
  };
  const [prevStats, prevStatsLoading] = useMetricStats(getPrevStatsProps);
  const [stats, loading, error] = useMetricStats(getCurrentStatsProps);
  const [timeSeriesStats, timeSeriesLoading] = useMetricStats(getTimeSeriesStatsProps);

  const datetimePickerProps: DatetimePickerProps = useMemo(() => {
    if (range) {
      /* istanbul ignore next */
      return {
        callback: ({ startDateTime, endDateTime }: DatetimePickerResult): void => {
          const startDate = new Date(startDateTime).setHours(0, 0, 0, 0);
          const endDate = new Date(endDateTime).setHours(23, 59, 59, 999);
          setStartDatetime(startDate);
          setEndDatetime(endDate);
        },
        dateMaximum: new DateTime(),
        dateMinimum: new DateTime().subtract(730, 'day'),
        format: 'DD/MM/YYYY',
        presetEndDatetime: today,
        presetStartDatetime: subDays(today, 1).setHours(0, 0, 0, 0),
        type: 'RANGE',
      };
    }
    return {
      callback: ({ date }: SingleDatetimePickerResult): void => {
        setDatetime(date);
      },
      dateMaximum: new DateTime(),
      format: 'DD/MM/YYYY',
      selectedDate: new Date(datetime).getTime().toString(),
      type: 'SINGLE',
    };
  }, [datetime, range, today]);

  const emptyStateProps: EmptyStateProps = useMemo(() => {
    return {
      caption: labels.emptyStateCaption,
      isLite: true,
      svg: ICON_CHART_WITH_DOWNLOADING,
      title: labels.emptyStateTitle,
    };
  }, [labels.emptyStateCaption, labels.emptyStateTitle]);

  const tabsProps: TabsProps = useMemo(() => {
    return {
      classes: 'monitoring-tabs',
      listLabel: 'monitoring-tabs',
      tabList: [
        {
          content: (
            <PageViewsTable
              chartData={{ content: timeSeriesStats?.timeSeriesSummaryPageViews, loading: timeSeriesLoading }}
              data={stats?.pageViews}
              displayRange={range}
              from={startDatetime}
              time={datetime}
              to={endDatetime}
            />
          ),
          key: 'page-views',
          label: labels.popularPages,
        },
        {
          content: (
            <PageLoadTable
              data={stats?.loadTime}
              displayRange={range}
              from={startDatetime}
              time={datetime}
              to={endDatetime}
            />
          ),
          key: 'page-load-time',
          label: labels.pageLoadTime,
        },
        {
          content: (
            <FileSizeTable
              data={stats?.fileSize}
              displayRange={range}
              from={startDatetime}
              time={datetime}
              to={endDatetime}
            />
          ),
          key: 'file-size',
          label: labels.fileSize,
        },
        {
          content: (
            <NotFoundErrorTable
              chartData={{ content: timeSeriesStats?.timeSeriesSummaryHttpStatus404, loading }}
              data={stats?.httpStatus404}
              displayRange={range}
              from={startDatetime}
              time={datetime}
              to={endDatetime}
            />
          ),
          key: '404-errors',
          label: labels['404Errors'],
        },
      ],
    };
  }, [
    datetime,
    endDatetime,
    labels,
    loading,
    range,
    startDatetime,
    stats?.fileSize,
    stats?.httpStatus404,
    stats?.loadTime,
    stats?.pageViews,
    timeSeriesLoading,
    timeSeriesStats?.timeSeriesSummaryHttpStatus404,
    timeSeriesStats?.timeSeriesSummaryPageViews,
  ]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const parseNestedPropertyToFloat = (obj: any, ...path: Array<string>): number | undefined => {
    const value = path.reduce((acc, key) => acc?.[key], obj);
    return value ? parseFloat(value) : undefined;
  };

  const renderDashboardSummaryItems = (): JSX.Element => {
    const isPageLoadAvailable = stats?.avgLoadTime?.data;
    const isPageViewsSummaryAvailable = stats?.summaryPageViews?.data;
    const isCacheHitRatioAvailable = stats?.avgCacheRatio?.data;

    const pageLoadData = parseNestedPropertyToFloat(stats, 'avgLoadTime', 'data');
    const pageLoadPrevData = parseNestedPropertyToFloat(prevStats, 'avgLoadTime', 'data');
    const pageViewsPrevData = parseNestedPropertyToFloat(prevStats, 'summaryPageViews', 'data');
    const cacheHitRatioPrevData = parseNestedPropertyToFloat(prevStats, 'avgCacheRatio', 'data');

    const items: Array<JSX.Element> = [];

    if (isPageLoadAvailable) {
      const pageLoadTimeFormatted = parseFloat(((pageLoadData as number) / 1000).toFixed(2));
      const prevPageLoadTimeFormatted = parseFloat(((pageLoadPrevData as number) / 1000).toFixed(2));

      items.push(
        <DashboardSummaryItem
          classes="page-load-time"
          count={pageLoadTimeFormatted}
          countUnit="s"
          description={labels.averagePageLoadTime}
          inverted
          key={labels.pageLoadTime}
          previousCount={prevPageLoadTimeFormatted}
          timePeriod={
            // istanbul ignore next
            calculateDateDifference > 1 ? DASHBOARD_SUMMARY_TIME_PERIOD.days : DASHBOARD_SUMMARY_TIME_PERIOD.day
          }
          timeValue={calculateDateDifference}
          title={labels.pageLoadTime}
          tooltip={labels.pageLoadTimeTooltipText}
        />,
      );
    }
    if (isPageViewsSummaryAvailable) {
      items.push(
        <DashboardSummaryItem
          classes="page-views"
          count={parseInt((stats.summaryPageViews as MetricStatValue).data)}
          description={labels.totalPageViews}
          key={labels.pageViews}
          previousCount={pageViewsPrevData}
          timePeriod={
            // istanbul ignore next
            calculateDateDifference > 1 ? DASHBOARD_SUMMARY_TIME_PERIOD.days : DASHBOARD_SUMMARY_TIME_PERIOD.day
          }
          timeValue={calculateDateDifference}
          title={labels.pageViews}
          tooltip={labels.pageViewsTooltipText}
        />,
      );
    }

    if (isCacheHitRatioAvailable) {
      items.push(
        <DashboardSummaryItem
          classes="cache-hit-ratio"
          count={parseInt((stats.avgCacheRatio as MetricStatValue).data)}
          countUnit="%"
          description={labels.cacheHitRatio}
          key={labels.cacheHitRatio}
          previousCount={cacheHitRatioPrevData}
          timePeriod={
            /* istanbul ignore next */
            calculateDateDifference > 1 ? DASHBOARD_SUMMARY_TIME_PERIOD.days : DASHBOARD_SUMMARY_TIME_PERIOD.day
          }
          timeValue={calculateDateDifference}
          title={labels.cacheHitRatio}
          tooltip={labels.cacheHitRatioTooltipText}
        />,
      );
    }

    if (!items.length && loading === USE_OBSERVABLE_STATE_COMPLETE) {
      return <EmptyState {...emptyStateProps} />;
    }

    if (error) {
      return <ErrorMessage message={labels.errorMessage} />;
    }

    return <>{items}</>;
  };

  const renderVariableScreenContent = (): JSX.Element => {
    if (loading === USE_OBSERVABLE_STATE_PENDING || prevStatsLoading === USE_OBSERVABLE_STATE_PENDING) {
      return <Loader />;
    }

    return (
      <>
        <DashboardSummary>{renderDashboardSummaryItems()}</DashboardSummary>
        <Tabs {...tabsProps} />
      </>
    );
  };

  return (
    <div className="monitoring-page-preview">
      <div className="monitoring-page-preview__date-picker-container">
        <div className="sds-form-label sds-form-label--visible">
          <label aria-controls="em-date-picker" className="form-label" htmlFor="em-date-picker">
            {range ? labels.dateRange : labels.date}
          </label>
        </div>
        <DatetimePicker id="em-date-picker" {...datetimePickerProps} />
        <FormControl
          checkboxCallback={handleSetRange}
          classes="em-range-checkbox"
          fieldLabel={labels.useCustomRange}
          id="range"
          name={'range-checkbox'}
          option={{ checked: range, key: 'range-checkbox', label: labels.useCustomRange }}
          tabIndex={0}
          type="checkbox"
        />
      </div>
      {renderVariableScreenContent()}
    </div>
  );
};
